import type { MasteryMapDocument } from '@ts-mastery-map/interfaces'

import { useDisciplineStore } from '@stores/discipline'
import { useMasteryMapStore } from '@stores/mastery-map'

import { useCsRoutes } from '@composables/routes/cs-route.hook'

let latestRequestId = 0

interface MasteryMapApiOptions {
  onError?: (err: unknown) => void
}

interface MasteryMapApiReturn {
  fetchMasteryMap: (assessmentId: number, options?: MasteryMapApiOptions) => Promise<void>
}

export const useMasteryMapApi = (): MasteryMapApiReturn => {
  const { $axios } = useNuxtApp()
  const disciplineStore = useDisciplineStore()
  const masteryMapStore = useMasteryMapStore()

  const fetchMasteryMap = async (assessmentId: number, options?: MasteryMapApiOptions): Promise<void> => {
    const disciplineId = disciplineStore.activeDiscipline?.id

    if (!disciplineId) {
      console.warn('missing disciplineId for mastery-map/mastery-map.hook.ts')

      return
    }

    const url = useCsRoutes('disciplines.mastery-map', {
      '[discipline_id]': disciplineId,
      '[assessment_id]': assessmentId,
    })

    if (!url) {
      console.warn('missing url for mastery-map/mastery-map.hook.ts')

      return
    }

    latestRequestId += 1
    const requestId = latestRequestId
    const isSuperseded = (): boolean => requestId !== latestRequestId

    try {
      masteryMapStore.setLoading(true)
      const { data } = await $axios.get(url)

      if (isSuperseded()) return

      if (data?.success) {
        masteryMapStore.setDocument(data?.data as MasteryMapDocument)
      }
    } catch (err: unknown) {
      console.error('unable to retrieve mastery-map/mastery-map.hook.ts:', err)

      if (isSuperseded()) return

      masteryMapStore.setDocument(null)
      options?.onError?.(err)
    } finally {
      if (!isSuperseded()) {
        masteryMapStore.setLoading(false)
      }
    }
  }

  return { fetchMasteryMap }
}
